// src/components/documents/DocumentStats.jsx - V1 ENTERPRISE
// ═══════════════════════════════════════════════════════════════════════════
// 📊 DOCUMENT STATS — Airbnb/Zillow/QuintoAndar Grade
// ═══════════════════════════════════════════════════════════════════════════
// 🔥 ULTRA ROBUSTO:    Cálculos con useMemo + zero re-renders innecesarios
// 🔥 ULTRA RÁPIDO:     React.memo + transiciones GPU-accelerated
// 🔥 ULTRA EFICIENTE:  Gráfico condicional (solo si hay datos)
// 🔥 ULTRA PROFESIONAL: KPIs Apple-style + gráfico doughnut interactivo
// ═══════════════════════════════════════════════════════════════════════════

import React, { useMemo, memo } from 'react';
import { motion } from 'framer-motion';
import {
  FileText,
  Image,
  Database,
  HardDrive,
  TrendingUp,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { ProfessionalChart } from '../../components/ProfessionalChart';

// ═══════════════════════════════════════════════════════════════════════════
// 📐 CONFIGURACIÓN DE KPIs
// ═══════════════════════════════════════════════════════════════════════════

const KPI_CONFIG = [
  { key: 'total', label: 'Total', icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50', ring: 'ring-blue-100' },
  { key: 'images', label: 'Imagens', icon: Image, color: 'text-emerald-600', bg: 'bg-emerald-50', ring: 'ring-emerald-100' },
  { key: 'pdfs', label: 'PDFs', icon: FileText, color: 'text-red-600', bg: 'bg-red-50', ring: 'ring-red-100' },
  { key: 'largeDocs', label: 'Cloud', icon: Database, color: 'text-purple-600', bg: 'bg-purple-50', ring: 'ring-purple-100' },
  { key: 'size', label: 'Espaço', icon: HardDrive, color: 'text-amber-600', bg: 'bg-amber-50', ring: 'ring-amber-100', format: 'size' },
];

// ═══════════════════════════════════════════════════════════════════════════
// 🎨 SUB-COMPONENTE: KPI CARD (Apple Style)
// ═══════════════════════════════════════════════════════════════════════════

const KpiCard = memo(function KpiCard({ config, value, isMobile }) {
  const displayValue = useMemo(() => {
    if (config.format === 'size' && typeof value === 'string') return value;
    return value ?? 0;
  }, [config.format, value]);

  const Icon = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`
        bg-white border border-slate-200 rounded-2xl
        hover:shadow-md transition-shadow duration-300
        ${isMobile ? 'p-3' : 'p-4'}
      `}
    >
      <div className="flex items-center justify-between mb-2">
        <div
          className={`
            flex items-center justify-center rounded-xl
            ${isMobile ? 'w-8 h-8' : 'w-10 h-10'}
            ${config.bg} ${config.ring} ring-1
          `}
        >
          <Icon
            size={isMobile ? 16 : 20}
            className={config.color}
            strokeWidth={1.5}
          />
        </div>
      </div>

      <p
        className={`font-extrabold text-slate-900 tracking-tight ${
          isMobile ? 'text-lg' : 'text-2xl'
        }`}
      >
        {displayValue}
        {config.format === 'size' && (
          <span className="text-sm font-normal text-slate-400 ml-0.5">MB</span>
        )}
      </p>

      <p
        className={`font-medium text-slate-500 mt-0.5 ${
          isMobile ? 'text-[10px]' : 'text-xs'
        }`}
      >
        {config.label}
      </p>
    </motion.div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// 📊 COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const DocumentStats = memo(function DocumentStats({
  stats,
  chartData,
  chartGrowth,
  showChart,
  onToggleChart,
  isMobile,
}) {
  // ─── KPIs calculados ─────────────────────────────────────────────────
  const kpiValues = useMemo(() => ({
    total: stats?.total ?? 0,
    images: stats?.images ?? 0,
    pdfs: stats?.pdfs ?? 0,
    largeDocs: stats?.largeDocs ?? 0,
    size: stats?.totalSizeMB ?? '0.00',
  }), [stats]);

  // ─── ¿Mostrar gráfico? ───────────────────────────────────────────────
  const hasChartData = chartData && chartData.length > 0;

  // ═══════════════════════════════════════════════════════════════════════
  // 🎨 RENDER
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <div className="space-y-4">
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* KPIs STRIP                                                   */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div
        className={`grid ${
          isMobile
            ? 'grid-cols-2 gap-2'
            : 'grid-cols-2 md:grid-cols-5 gap-3 md:gap-4'
        }`}
      >
        {KPI_CONFIG.map((config) => (
          <KpiCard
            key={config.key}
            config={config}
            value={kpiValues[config.key]}
            isMobile={isMobile}
          />
        ))}
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* GRÁFICO (toggleable)                                         */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {hasChartData && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {/* Header del gráfico */}
          <button
            onClick={onToggleChart}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <h3
                className={`font-semibold text-slate-800 ${
                  isMobile ? 'text-sm' : 'text-base'
                }`}
              >
                Distribuição de Documentos
              </h3>
              {chartGrowth && (
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    chartGrowth.trend === 'up'
                      ? 'bg-emerald-100 text-emerald-700'
                      : chartGrowth.trend === 'down'
                      ? 'bg-rose-100 text-rose-700'
                      : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  <TrendingUp size={10} strokeWidth={2} />
                  {chartGrowth.formatted}
                </span>
              )}
            </div>
            {isMobile ? (
              showChart ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />
            ) : (
              <span className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
                {showChart ? 'Ocultar' : 'Mostrar'}
              </span>
            )}
          </button>

          {/* Contenido del gráfico */}
          {showChart && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="px-4 pb-4"
            >
              <ProfessionalChart
                type="doughnut"
                data={chartData}
                title=""
                height={isMobile ? 220 : 280}
                colors={['#10b981', '#ef4444', '#3b82f6', '#8b5cf6']}
                showLegend={true}
                showStats={false}
                showExport={!isMobile}
                glassmorphism={true}
                gradient={true}
                currency={false}
                percentage={true}
              />
            </motion.div>
          )}
        </div>
      )}
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// 📤 EXPORT
// ═══════════════════════════════════════════════════════════════════════════

DocumentStats.displayName = 'DocumentStats';
export default DocumentStats;